/**
 * Token utilities for handling Auth0 token retrieval with retry logic
 */

export const TOKEN_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 2000, // 2 seconds
  TOKEN_TIMEOUT: 15, // 15 seconds
  REAUTH_GUARD_TTL_MS: 30000, // 30 seconds
};

const REAUTH_GUARD_KEY = "auth0_reauth_in_progress";
const REAUTH_IN_PROGRESS_MESSAGE = "reauth_in_progress";

/**
 * Determines if an error is auth/session-related
 * @param {Error|Object} error - The error object to check
 * @returns {boolean} - True if the error is auth/session-related
 */
export const isAuthSessionError = (error) => {
  if (!error) return false;

  const errorMessage = (error.message || error.toString()).toLowerCase();
  const statusCode = error.status || error.statusCode || error.response?.status;
  const errorCode = (error.error || error.code || "").toLowerCase();

  return (
    statusCode === 401 ||
    statusCode === 403 ||
    errorCode.includes("login_required") ||
    errorCode.includes("consent_required") ||
    errorCode.includes("invalid_grant") ||
    errorMessage.includes("missing refresh token") ||
    errorMessage.includes("login_required") ||
    errorMessage.includes("consent_required") ||
    errorMessage.includes("invalid_grant") ||
    errorMessage.includes("session") ||
    errorMessage.includes("unauthorized") ||
    errorMessage.includes("forbidden") ||
    errorMessage.includes("token") ||
    errorMessage.includes("jwt") ||
    errorMessage.includes("auth")
  );
};

export const isReauthInProgressError = (error) => {
  if (!error) return false;
  return (error.message || "").toLowerCase() === REAUTH_IN_PROGRESS_MESSAGE;
};

const hasRecentReauthRedirect = () => {
  if (typeof window === "undefined") return false;
  const timestamp = Number(sessionStorage.getItem(REAUTH_GUARD_KEY));
  if (!timestamp) return false;
  return Date.now() - timestamp < TOKEN_CONFIG.REAUTH_GUARD_TTL_MS;
};

const markReauthRedirect = () => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(REAUTH_GUARD_KEY, String(Date.now()));
};

export const clearReauthGuard = () => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(REAUTH_GUARD_KEY);
};

/**
 * Redirects users back through Auth0 when their session can no longer mint tokens.
 * Guarded to avoid redirect loops.
 */
export const triggerReauth = async (loginWithRedirect, returnTo = "/dashboard") => {
  if (hasRecentReauthRedirect()) return false;
  markReauthRedirect();

  await loginWithRedirect({
    appState: { returnTo },
    authorizationParams: { prompt: "login" },
  });

  return true;
};

/**
 * Enhanced token retrieval function with retry logic
 * @param {Function} getAccessTokenSilently - Auth0's getAccessTokenSilently function
 * @param {Object} options - Configuration options
 * @param {string} options.audience - The audience for the token
 * @param {string} options.context - Context for logging (e.g., 'dashboard', 'keys')
 * @returns {Promise<string>} - The access token
 */
export const getValidTokenWithRetry = async (
  getAccessTokenSilently,
  { audience, context = "unknown" }
) => {
  const { MAX_RETRIES, RETRY_DELAY, TOKEN_TIMEOUT } = TOKEN_CONFIG;

  const attemptTokenRetrieval = async (attempt = 1) => {
    try {
      console.log(`Getting token for ${context} - attempt ${attempt}`);

      const token = await getAccessTokenSilently({
        audience,
        // First pass prefers cached token; retries force refresh.
        ignoreCache: attempt > 1,
        timeoutInSeconds: TOKEN_TIMEOUT,
      });

      if (!token) {
        throw new Error("No token received from Auth0");
      }

      console.log(`Token obtained successfully for ${context}`);
      return token;
    } catch (error) {
      console.error(
        `Token retrieval failed for ${context} (attempt ${attempt}):`,
        error
      );

      if (attempt < MAX_RETRIES) {
        console.log(`Retrying token retrieval in ${RETRY_DELAY}ms...`);
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return attemptTokenRetrieval(attempt + 1);
      }

      throw new Error(
        `Failed to get valid token after ${MAX_RETRIES} attempts: ${error.message}`
      );
    }
  };

  return attemptTokenRetrieval();
};

/**
 * Creates a standardized error message for token-related errors
 * @param {Error} error - The original error
 * @param {string} context - The context where the error occurred
 * @returns {string} - User-friendly error message
 */
export const getTokenErrorMessage = (error, context = "") => {
  if (isAuthSessionError(error)) {
    return "Session expired. Please refresh the page or log in again.";
  }

  return `Error in ${context}: ${error.message || "Unknown error occurred"}`;
};

/**
 * Higher-order function to wrap API calls with token retry logic
 * @param {Function} getAccessTokenSilently - Auth0's getAccessTokenSilently function
 * @param {Object} options - Configuration options
 * @returns {Function} - Enhanced API call function
 */
export const withTokenRetry = (getAccessTokenSilently, { audience, context }) => {
  return async (apiCall) => {
    try {
      const token = await getValidTokenWithRetry(getAccessTokenSilently, {
        audience,
        context,
      });
      return await apiCall(token);
    } catch (error) {
      console.error(`API call failed in ${context}:`, error);
      throw error;
    }
  };
};

/**
 * Higher-order wrapper for authenticated API calls with session-recovery behavior.
 */
export const withTokenRecovery =
  ({ getAccessTokenSilently, loginWithRedirect, audience, context, returnTo }) =>
  async (apiCall) => {
    try {
      const token = await getValidTokenWithRetry(getAccessTokenSilently, {
        audience,
        context,
      });
      clearReauthGuard();
      return await apiCall(token);
    } catch (error) {
      if (isAuthSessionError(error)) {
        try {
          await triggerReauth(loginWithRedirect, returnTo);
        } catch (redirectError) {
          console.error(`Re-auth redirect failed in ${context}:`, redirectError);
        }
        throw new Error(REAUTH_IN_PROGRESS_MESSAGE);
      }
      console.error(`API call failed in ${context}:`, error);
      throw error;
    }
  };
